import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UserProfile } from '../types';
import type { TutorialKapitelId, TutorialRolle } from '../tutorial/types';
// `rollenAusProfil` steht bei den Typen, nicht im Register: es bildet eine
// Profilrolle auf Lernrollen ab und kennt dafuer kein einziges Kapitel.
import { lernrollenHaengenAnRollen, rollenAusProfil } from '../tutorial/types';
import { kapitelFuerRolle, lernpfad } from '../tutorial/register';
import {
    Fortschritt,
    KapitelDaten,
    ladeEigeneRollen,
    ladeFortschritt,
    ladeKapitelInhalte,
    markiereAbgeschlossen as schreibeAbgeschlossen,
    markiereGesehen as schreibeGesehen,
    markiereTourAbgebrochen as schreibeTourAbbruch,
} from '../services/tutorial/tutorialService';

/** Stabil, damit `kapitel`/`pfad` ohne Profil nicht bei jedem Rendern neu entstehen. */
const KEINE_ROLLEN: TutorialRolle[] = [];

/**
 * LERNROLLEN des angemeldeten Profils — Systemrolle plus fachliche Rollen
 * (`rollenAusProfil`, Zwilling von `tutorial_rollen()` Nr. 173).
 *
 * Nur `user` und `arbeitsvorbereitung` fragen `benutzer_rollen` (ein Abruf je
 * Anmeldung und Montage). Bis die Antwort da ist — und wenn sie ausbleibt
 * (Migration offen, Netz) —, gilt die Systemrolle allein, wie vor dem
 * Rollensystem. `null` = kein Profil.
 *
 * Das Ergebnis bleibt dasselbe Array, bis sich Systemrolle oder Rollen
 * ändern — taugt als Abhängigkeit in `useMemo`/`useEffect`.
 */
export const useLernRollen = (userProfile: UserProfile | null): TutorialRolle[] | null => {
    const rolle = userProfile?.role;
    const userId = userProfile?.id;
    const fragt = lernrollenHaengenAnRollen(rolle);

    const [geladen, setGeladen] = useState<{ userId: string; rollen: string[] | undefined } | null>(null);

    useEffect(() => {
        if (!fragt || !userId) return;
        let abgebrochen = false;
        // Kein setState im Effektrumpf selbst (react-hooks/set-state-in-effect,
        // CI-Ratsche) — nur in der Antwort. Bis dahin gilt die Systemrolle.
        void ladeEigeneRollen(userId).then((rollen) => {
            if (!abgebrochen) setGeladen({ userId, rollen });
        });
        return () => { abgebrochen = true; };
    }, [fragt, userId]);

    // Eine Antwort für eine frühere Anmeldung zählt nicht.
    const fachrollen = fragt && geladen && geladen.userId === userId ? geladen.rollen : undefined;
    // Als Zeichenkette in die Abhängigkeiten: ein neues Array mit denselben
    // Rollen soll kein neues Ergebnis erzeugen. `ladeEigeneRollen` sortiert.
    const schluessel = fachrollen ? fachrollen.join(',') : '';

    return useMemo(
        () => (rolle ? rollenAusProfil(rolle, schluessel ? schluessel.split(',') : undefined) : null),
        [rolle, schluessel],
    );
};

/**
 * LERNZENTRUM — Laden und Fortschritt an einer Stelle.
 *
 * Der Hook holt Kapitelinhalte und Fortschritt EINMAL je Montage und haelt
 * beides als Map. Kein Polling, kein Realtime-Kanal: Kapitel aendern sich mit
 * einem Pipeline-Lauf (Paket C), nicht waehrend jemand liest, und der eigene
 * Fortschritt wird nur hier geschrieben.
 *
 * Die Anzeige haengt NICHT am Ladezustand: Titel, Schritte, Praxisrahmen und
 * Stolperfallen stehen im Register bzw. im Bundle. Was aus der Datenbank kommt
 * (Video, Screenshots, ausformulierter Text, Fortschritt), reichert an — fehlt
 * es, ist das Lernzentrum vollstaendig benutzbar. Deshalb faengt `laedt` auch
 * keine Ansicht ab, sondern faerbt nur den Fortschrittsbalken.
 */

export interface LernzentrumStand {
    /** Die Kapitel, die dieses Profil sehen darf — Registerreihenfolge. */
    kapitel: ReturnType<typeof kapitelFuerRolle>;
    /** Dieselben Kapitel, topologisch nach Voraussetzungen sortiert. */
    pfad: ReturnType<typeof lernpfad>;
    inhalte: ReadonlyMap<TutorialKapitelId, KapitelDaten>;
    fortschritt: ReadonlyMap<TutorialKapitelId, Fortschritt>;
    laedt: boolean;
    /** Kapitel geoeffnet — merkt `gesehen_am`, sofort sichtbar, Schreiben im Hintergrund. */
    merkeGesehen: (id: TutorialKapitelId) => void;
    /** „Kapitel abschließen" unten auf der Kapitelseite. */
    merkeAbgeschlossen: (id: TutorialKapitelId) => void;
    /**
     * Tour weggeklickt — passt auf `useTour({ merkeAbbruch })`.
     *
     * Ohne Anzeigezustand: der Abbruch ist eine Randnotiz fuer die Auswertung,
     * kein Fortschritt. Er faerbt im Lernzentrum nichts ein und braucht
     * deshalb auch keinen optimistischen Eintrag in `fortschritt`.
     */
    merkeAbbruch: (id: TutorialKapitelId, schrittIndex: number) => void;
}

export const useLernzentrum = (userProfile: UserProfile | null): LernzentrumStand => {
    const userId = userProfile?.id;

    const [inhalte, setInhalte] = useState<ReadonlyMap<TutorialKapitelId, KapitelDaten>>(() => new Map());
    const [fortschritt, setFortschritt] = useState<ReadonlyMap<TutorialKapitelId, Fortschritt>>(() => new Map());
    const [laedt, setLaedt] = useState(true);

    const rollen = useLernRollen(userProfile) ?? KEINE_ROLLEN;
    const kapitel = useMemo(() => kapitelFuerRolle(rollen), [rollen]);
    const pfad = useMemo(() => lernpfad(rollen), [rollen]);

    useEffect(() => {
        let abgebrochen = false;
        // Kein `setLaedt(true)` hier: `laedt` startet auf true, und ein
        // synchrones setState im Effektrumpf erzeugt eine Kaskadenrendierung
        // (react-hooks/set-state-in-effect, CI-Ratsche). Ein Nutzerwechsel
        // ohne Neuladen der Seite laesst den Balken deshalb kurz auf dem
        // alten Stand stehen — das ist der billigere der beiden Fehler.
        //
        // Beide Abrufe zusammen: sie haengen nicht voneinander ab, und
        // nacheinander waere es der doppelte Rundlauf bis zum ersten Bild.
        void Promise.all([ladeKapitelInhalte(), ladeFortschritt(userId)]).then(([geladeneInhalte, geladenerFortschritt]) => {
            if (abgebrochen) return;
            setInhalte(geladeneInhalte);
            setFortschritt(geladenerFortschritt);
            setLaedt(false);
        });
        return () => { abgebrochen = true; };
    }, [userId]);

    /**
     * Optimistisch: die Marke steht sofort im State, das Schreiben laeuft
     * daneben. Scheitert es (Tabelle fehlt noch, kein Netz), bleibt die Anzeige
     * fuer diese Sitzung richtig und ist beim naechsten Laden wieder weg — das
     * ist die harmlosere Haelfte der beiden moeglichen Irrtuemer.
     */
    const merkeGesehen = useCallback((id: TutorialKapitelId) => {
        setFortschritt((alt) => {
            const vorhanden = alt.get(id);
            if (vorhanden?.gesehenAm) return alt;
            const neu = new Map(alt);
            neu.set(id, {
                kapitelId: id,
                gesehenAm: new Date().toISOString(),
                abgeschlossenAm: vorhanden?.abgeschlossenAm ?? null,
            });
            return neu;
        });
        void schreibeGesehen(id);
    }, []);

    const merkeAbgeschlossen = useCallback((id: TutorialKapitelId) => {
        const jetzt = new Date().toISOString();
        setFortschritt((alt) => {
            const neu = new Map(alt);
            const vorhanden = alt.get(id);
            neu.set(id, { kapitelId: id, gesehenAm: vorhanden?.gesehenAm ?? jetzt, abgeschlossenAm: jetzt });
            return neu;
        });
        void schreibeAbgeschlossen(id);
    }, []);

    const merkeAbbruch = useCallback((id: TutorialKapitelId, schrittIndex: number) => {
        void schreibeTourAbbruch(id, schrittIndex);
    }, []);

    return { kapitel, pfad, inhalte, fortschritt, laedt, merkeGesehen, merkeAbgeschlossen, merkeAbbruch };
};
